import React from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const resolveImage = (src, fallback = '') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const renderParagraphs = (raw, fallback = '') => {
  const text = raw || fallback;
  const parts = String(text).replace(/\r/g, '').split(/\n\s*\n|\n/).map(s => s.trim()).filter(Boolean);
  return parts.map((p, i) => <p key={i} className="mb-4 text-muted leading-relaxed text-[15px]">{p}</p>);
};

const CurriculumPage = () => {
  const pageData = usePage('curriculum');
  const sections = pageData.sections || {};

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || pageData.title || "CBSE Curriculum";
  const bannerSubtitle = banner.subtitle || "Holistic, Value-Based and Future-Focused Educational Framework";
  const bannerBreadcrumb = banner.breadcrumb || "CURRICULUM";
  const bannerImgSrc = pageData.bannerImage || '/images/page-banners/curricullam-infrastructure.jpg';

  const pedagogy = sections.pedagogy || "St. Andrews World School follows the Central Board of Secondary Education (CBSE) curriculum, recognized worldwide for its academic depth and alignment with competitive examination frameworks.\n\nOur pedagogy bridges conceptual fundamentals with 21st-century competencies: inquiry, coding, sports, and fine arts.";
  const curriculumImg = resolveImage(sections.image, '/images/library.JPG');

  const subjects = (sections.subjects_list || "Languages (English, Hindi, Sanskrit) | Mathematics & Applied Logic | General Science, Physics, Chemistry, Biology | Social Sciences & Civic Studies | Artificial Intelligence & Coding | Sports, Physical Education & Yoga | Performing & Fine Arts").split('|').map(s => s.trim()).filter(Boolean);

  const features = sections.features || [
    { title: 'Experiential Science', desc: 'Laboratory sessions making concepts come alive through observation and testing.', icon: 'fa-atom' },
    { title: 'Coding & AI Education', desc: 'Introducing computational thinking, algorithm basics, and creative software design.', icon: 'fa-code' },
    { title: 'Language Fluency', desc: 'Extensive reading programs, elocution, creative writing, and public debates.', icon: 'fa-language' },
    { title: 'Sports & Physical Fitness', desc: 'Structured athletics, swimming, team games, and yogic mindfulness.', icon: 'fa-volleyball' },
  ];

  return (
    <>
      <SEO
        title={pageData.title || "CBSE Curriculum"}
        description="Comprehensive CBSE curriculum details, academic subjects, and 21st-century skill integration at St. Andrews World School, Sonipat."
        keywords="CBSE Curriculum, Academic Subjects, Coding, Science Labs, Sonipat School, SAWS Sonipat"
      />

      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        bgImage={bannerImgSrc}
      />


      {/* PEDAGOGY OVERVIEW */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="kicker mb-2">Curriculum Pedagogy</div>
                <h2 className="h-section mb-6">Value-Based, Innovative & Experiential</h2>
                <div className="divider-line mb-6" />
                <div className="space-y-2 mb-8">
                  {renderParagraphs(pedagogy)}
                </div>

                {subjects.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-primary mb-3">
                      Core Academic Disciplines
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {subjects.map((subj, sIdx) => (
                        <span
                          key={sIdx}
                          className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/5 text-primary border border-primary/10 inline-flex items-center gap-1.5"
                        >
                          <i className="fas fa-check text-accent text-[10px]" />
                          <span>{subj}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delayMs={150}>
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-100 group">
                  <img
                    src={curriculumImg}
                    alt="Curriculum Pedagogy"
                    className="w-full h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent flex items-end p-6">
                    <span className="text-white text-xs font-semibold uppercase tracking-wider bg-accent/90 px-3 py-1 rounded-full">
                      CBSE Affiliated Excellence
                    </span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 4 FEATURE PILLARS */}
      <section className="section bg-surface-alt">
        <div className="container">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="kicker">Core Features</div>
              <h2 className="h-section mt-2">Pillars of Academic Excellence</h2>
              <div className="w-16 h-1 bg-accent mx-auto mt-4 rounded-full" />
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feat, idx) => (
              <Reveal key={idx} delayMs={idx * 80}>
                <div className="card p-6 text-center h-full hover:shadow-lg transition-all duration-300 border border-gray-100">
                  <div className="w-14 h-14 rounded-2xl bg-accent/15 text-accent flex items-center justify-center mx-auto mb-5 text-xl">
                    <i className={`fas ${feat.icon || 'fa-star'}`} />
                  </div>
                  <h3 className="font-serif font-bold text-base text-primary mb-2">
                    {feat.title}
                  </h3>
                  <p className="text-muted text-xs leading-relaxed">
                    {feat.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default CurriculumPage;
